import React, { useState, useEffect, useCallback, useContext } from "react";
import { message, Button, Tooltip } from "antd";
import { utils, writeFileXLSX } from "xlsx";
import { Sidebar, Footer, ContainerDiv, LoadingIcon } from "../../components";
import { COLORS } from "../../consts";
import RadarChart from "./RadarChart";
import Dropdowns from "./Dropdowns";
import { getGraphData, getAllPlotData } from "../../utils/api";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";

const Analysis = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState([]);
  const [graphData, setGraphData] = useState({});
  const [currentOrg, setCurrentOrg] = useState(null);
  const [currentSite, setCurrentSite] = useState(null);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [currentCrop, setCurrentCrop] = useState(null);
  const [currentMetric, setCurrentMetric] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [radarData, setRadarData] = useState({});
  const [graphLoading, setGraphLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [allPlotData, setAllPlotData] = useState([]);
  const [fetchingAllPlots, setFetchingAllPlots] = useState(false);

  // CurrentOrg - id of the org
  // CurrentSite - {name, id , metadata}
  // CurrentCrop - {name, id}
  // CurrentPlot = [{name, id}]
  // CurrentMetric - name of the metric
  // CurrentDate - [dateString]

  // Fetch graph data
  const fetchGraphData = async () => {
    try {
      setGraphLoading(true);
      const graphRes = await getGraphData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        currentPlot?.map((plot) => plot.id),
        currentDate,
      );
      const data = graphRes?.data?.data;
      console.log({ data });
      setGraphData(data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Oops! Error fetching graph data!";
      messageApi.open({
        type: "error",
        content: msg,
      });
    } finally {
      setGraphLoading(false);
    }
  };

  const fetchAllPlotData = async () => {
    try {
      const graphRes = await getAllPlotData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
      );
      const data = graphRes?.data?.data;
      console.log({ data });
      setAllPlotData(data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Oops! Error fetching graph data!";
      messageApi.open({
        type: "error",
        content: msg,
      });
    } finally {
      setFetchingAllPlots(false);
    }
  };

  // Set radar graph data
  useEffect(() => {
    if (currentMetric) {
      const graphNames = Object.keys(graphData) || null;
      console.log({ graphNames });
      const res = {};
      Object.values(graphData)?.forEach((plotData) => {
        plotData?.forEach((plotDateData) => {
          if (
            plotDateData?.harvest_date &&
            !(plotDateData.harvest_date in res)
          ) {
            res[plotDateData.harvest_date] = {
              name: plotDateData.harvest_date,
              val: [],
            };
          }
          res[plotDateData.harvest_date].val.push(
            plotDateData.data[getMetricValue(currentMetric)],
          );
        });
      });
      setRadarData({ labels: graphNames, data: Object.values(res) });
    } else {
      setRadarData({});
    }
  }, [currentMetric, graphData]);

  const exportFile = useCallback(() => {
    const wb = utils.book_new();
    const flatData = [];
    for (let [key, value] of Object.entries(graphData)) {
      value.forEach((plotDateData) => {
        flatData.push({
          plot: key,
          harvest_date: plotDateData.harvest_date,
          ...plotDateData.data,
        });
      });
    }
    const ws = utils.json_to_sheet(flatData);
    utils.book_append_sheet(wb, ws, "Sheet1");

    writeFileXLSX(wb, `analysis_${currentSite.name}.xlsx`);
  }, [graphData, currentSite]);

  const exportFiles = useCallback(async () => {
    setFetchingAllPlots(true);
    message.info("Fetching plots...");

    try {
      const res = await getAllPlotData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
      );

      const wb = utils.book_new();
      Object.entries(res?.data?.data).forEach((dateData) => {
        const flatData = [];
        const dateStr = dateData[0];

        // Looping through data for each object where {key = date, value = { key = plotName, value = data}}
        Object.entries(dateData[1]).forEach((plotData) => {
          flatData.push({ Plot: plotData[0], ...plotData[1] });
        });

        const ws = utils.json_to_sheet(flatData);
        utils.book_append_sheet(wb, ws, dateStr);
      });

      writeFileXLSX(wb, `data_${currentSite?.name}.xlsx`);
    } catch (err) {
      console.log(err);
      message.error("Error fetching plot data!");
    } finally {
      setFetchingAllPlots(false);
    }
  }, [currentOrg, currentSite, currentCrop]);

  const isDictionary = (variable) => {
    return (
      typeof variable === "object" &&
      variable !== null &&
      !Array.isArray(variable)
    );
  };

  const getMetricLabel = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.label;
    }
    return "Unknown Metric";
  };

  const getMetricValue = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.value;
    }
    return "Unknown Metric";
  };

  return (
    <ContainerDiv>
      {contextHolder}
      <div id="analysis">
        <Sidebar
          active="analysis"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />
        <div
          className={`flex flex-col w-full px-20 ${
            sidebarOpen ? "pl-60" : ""
          } pt-10 md:pt-4 pb-14 md:px-15 sm:pr-10 justify-center transition-padding ease-in-out duration-200`}
        >
          <Dropdowns
            setSiteMetadata={setSiteMetadata}
            currentMetric={currentMetric}
            setCurrentMetric={setCurrentMetric}
            metricType={metricType}
            setMetricType={setMetricType}
            currentOrg={currentOrg}
            setCurrentOrg={setCurrentOrg}
            currentSite={currentSite}
            setCurrentSite={setCurrentSite}
            currentCrop={currentCrop}
            setCurrentCrop={setCurrentCrop}
            currentPlot={currentPlot}
            setCurrentPlot={setCurrentPlot}
            fetchGraphData={fetchGraphData}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            setDropdownLoading={setDropdownLoading}
          />
          {Object.keys(siteMetadata).length === 0 && !dropdownLoading ? (
            <div className="text-2xl text-center">Metadata not Configured!</div>
          ) : (
            <>
              {!dropdownLoading ? (
                <div className="flex justify-end mb-5 downloadButtons">
                  <Tooltip
                    title={
                      graphLoading
                        ? ""
                        : `Export plot details for ${currentCrop?.name} on ${currentDate} to excel`
                    }
                    color={COLORS.primaryBlue}
                  >
                    <Button
                      type="primary"
                      icon={
                        graphLoading ? (
                          <LoadingOutlined />
                        ) : (
                          <DownloadOutlined />
                        )
                      }
                      onClick={exportFile}
                      disabled={graphLoading}
                      className="mr-2 hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                    >
                      Export Data
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={
                      graphLoading ? "" : `Export all plot details to excel`
                    }
                    color={COLORS.primaryBlue}
                  >
                    <Button
                      type="primary"
                      icon={
                        graphLoading ? (
                          <LoadingOutlined />
                        ) : (
                          <DownloadOutlined />
                        )
                      }
                      onClick={exportFiles}
                      className="hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                      disabled={fetchingAllPlots}
                    >
                      Export All Data
                    </Button>
                  </Tooltip>
                </div>
              ) : null}

              {!graphLoading ? (
                <>
                  {radarData.data?.length ? (
                    <RadarChart
                      radarData={radarData}
                      currentMetric={getMetricLabel(currentMetric)}
                      metricUnit={
                        currentSite?.metadata[currentCrop.name]?.units[
                          getMetricValue(currentMetric)
                        ]
                          ? `(${
                              currentSite?.metadata[currentCrop.name]?.units[
                                getMetricValue(currentMetric)
                              ]
                            })`
                          : ""
                      }
                    />
                  ) : null}
                </>
              ) : (
                <LoadingIcon height="200px" />
              )}
            </>
          )}
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </ContainerDiv>
  );
};

export default Analysis;
